import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FAMILLE_LARGE_SEUIL, type ArticleFamily } from '@kn/shared';
import { api } from '../../api/client';
import { messageErreur } from '../../api/error-message';

function formaterNombre(valeur: string): string {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre)) return valeur;
  return nombre.toLocaleString('fr-FR', { maximumFractionDigits: 3 });
}

/**
 * Ce qui fonde une proposition de famille : les fabrications FAB du journal,
 * **composé (père) → composants (fils)**, avec les libellés.
 *
 * Une proposition ne se valide pas sur une liste de codes : c'est la matière
 * qui décide, et c'est elle qu'on montre.
 */
export function FamilleFabrications({ famille }: { famille: ArticleFamily }): ReactElement {
  const preuves = useQuery({
    queryKey: ['families', famille.id, 'fabrications'],
    queryFn: () => api.familyEvidence(famille.id),
  });

  const large = famille.members.length > FAMILLE_LARGE_SEUIL;

  return (
    <div className="carte">
      <h2>
        Fabrications à l&apos;origine de la proposition &mdash; {famille.members.length} code(s)
      </h2>

      {large && (
        <p className="alerte alerte--info">
          Cette proposition regroupe {famille.members.length} codes, au-delà du repère de{' '}
          {FAMILLE_LARGE_SEUIL}. Ce n&apos;est pas un plafond : une famille large peut être juste.
          Vérifiez que toutes les fabrications ci-dessous décrivent bien le même article.
        </p>
      )}

      <ul className="liste-cles">
        {famille.members.map((membre) => (
          <li key={membre.codpro}>
            {membre.codpro} {membre.designation ? `· ${membre.designation}` : ''}
          </li>
        ))}
      </ul>

      {preuves.isPending && <p className="chargement">Chargement des fabrications…</p>}
      {preuves.isError && <p className="alerte alerte--erreur">{messageErreur(preuves.error)}</p>}

      {preuves.data && preuves.data.fabrications.length === 0 && (
        <p className="alerte alerte--info" role="status">
          Aucune fabrication FAB au journal pour ces codes. Cette famille a probablement été créée à
          la main.
        </p>
      )}

      {preuves.data && preuves.data.fabrications.length > 0 && (
        <>
          <p className="sous-titre">
            {preuves.data.fabrications.length} fabrication(s), du composé vers ses composants.
          </p>
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Référence</th>
                <th scope="col">Jour</th>
                <th scope="col">Composé (père)</th>
                <th scope="col">UVC</th>
                <th scope="col">Composants (fils)</th>
                <th scope="col">Écritures</th>
              </tr>
            </thead>
            <tbody>
              {preuves.data.fabrications.map((fabrication) => (
                <tr key={fabrication.reference}>
                  <td>{fabrication.reference}</td>
                  <td>{fabrication.jour ?? '—'}</td>
                  <td>
                    <strong>{fabrication.compose.codpro}</strong>
                    {fabrication.compose.designation && (
                      <div className="sous-titre">{fabrication.compose.designation}</div>
                    )}
                  </td>
                  <td className="droite">{formaterNombre(fabrication.compose.uvc)}</td>
                  <td>
                    {fabrication.composants.map((composant) => (
                      <div key={`${fabrication.reference}-${composant.codpro}`}>
                        {composant.codpro} · {formaterNombre(composant.uvc)} UVC
                        {composant.designation ? ` · ${composant.designation}` : ''}
                      </div>
                    ))}
                  </td>
                  <td className="droite">{fabrication.ecritures.toLocaleString('fr-FR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {preuves.data && preuves.data.liensCopacking.length > 0 && (
        <>
          <h3>Liens de copacking déclarés</h3>
          <p className="sous-titre">
            Table de référence, indépendante du journal : elle confirme — ou contredit — ce que les
            fabrications montrent.
          </p>
          <table className="tableau">
            <thead>
              <tr>
                <th scope="col">Table</th>
                <th scope="col">Composé</th>
                <th scope="col">Composant</th>
                <th scope="col">Quantité</th>
              </tr>
            </thead>
            <tbody>
              {preuves.data.liensCopacking.map((lien) => (
                <tr key={`${lien.source}-${lien.compose}-${lien.composant}`}>
                  <td>{lien.source === 'COPACKING' ? 'Copacking' : 'Nomenclature'}</td>
                  <td>{lien.compose}</td>
                  <td>{lien.composant}</td>
                  <td className="droite">
                    {lien.quantite === null ? '—' : formaterNombre(lien.quantite)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
